import { useColorMode } from '@chakra-ui/react';
import { RainbowKitProvider, darkTheme, lightTheme } from '@rainbow-me/rainbowkit';
import type { Locale as RainbowKitLocale } from '@rainbow-me/rainbowkit';
import React from 'react';
import { WagmiProvider } from 'wagmi';

import config from 'configs/app';
import { useI18n } from 'lib/i18n/context';
import type { Locale } from 'lib/i18n/core';
import useEnforceSingleWallet from 'lib/web3/useEnforceSingleWallet';
import wagmiConfig from 'lib/web3/wagmiConfig';
import colors from 'theme/foundations/colors';
import { BODY_TYPEFACE } from 'theme/foundations/typography';

const feature = config.features.blockchainInteraction;

// Map the app's locales to RainbowKit's supported locales.
const RAINBOWKIT_LOCALE_MAP: Record<Locale, RainbowKitLocale> = {
  en: 'en',
  zh: 'zh-CN',
  es: 'es',
  ja: 'ja',
  ko: 'ko',
};

interface Props {
  children: React.ReactNode;
}

// Provides the wagmi config. Must sit ABOVE the QueryClientProvider in the tree,
// matching the canonical wagmi + RainbowKit setup order.
const Web3ModalProvider = ({ children }: Props) => {
  return (
    <WagmiProvider config={ wagmiConfig }>
      { children }
    </WagmiProvider>
  );
};

// Provides the RainbowKit connect/account modals. Must sit INSIDE the
// WagmiProvider, the QueryClientProvider and the I18nProvider. When blockchain
// interaction is disabled it renders nothing extra so the rest of the app is
// unaffected.
export const Web3WalletUIProvider = ({ children }: Props) => {
  const { locale } = useI18n();
  const { colorMode } = useColorMode();

  useEnforceSingleWallet();

  // The wallet modal follows the app's light/dark color mode.
  const theme = React.useMemo(() => {
    const themeFn = colorMode === 'dark' ? darkTheme : lightTheme;
    const rainbowTheme = themeFn({
      accentColor: colors.blue[600],
      accentColorForeground: 'white',
      borderRadius: 'small',
    });
    rainbowTheme.fonts.body = `${ BODY_TYPEFACE }, sans-serif`;
    return rainbowTheme;
  }, [ colorMode ]);

  if (!feature.isEnabled) {
    return children;
  }

  return (
    <RainbowKitProvider
      theme={ theme }
      modalSize="wide"
      locale={ RAINBOWKIT_LOCALE_MAP[locale] ?? 'en' }
    >
      { children }
    </RainbowKitProvider>
  );
};

export default Web3ModalProvider;
